import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

const groepColors = {
  'Groen': '#2D6A4F', 'Geel': '#E67E22', 'Rood': '#C0392B',
  'Blauw': '#2980B9', 'Toekomstheld': '#8E44AD'
};

function PlanModal({ item, users, onClose, onSave }) {
  const [form, setForm] = useState(item || {
    userId: '', titel: '', datum: '', tijdVan: '', tijdTot: '',
    locatie: '', groep: '', notitie: ''
  });
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 20 }}>{item ? 'Planning bewerken' : 'Shift inplannen'}</h2>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button>
        </div>

        <div className="field"><label>Vrijwilliger <span className="req">*</span></label>
          <select className="input" value={form.userId} onChange={e => setField('userId', e.target.value)}>
            <option value="">Kies vrijwilliger...</option>
            {users.filter(u => u.profileComplete).map(u => (
              <option key={u.id} value={u.id}>
                {u.voornaam ? `${u.voornaam} ${u.achternaam || ''}` : u.email}
              </option>
            ))}
          </select>
        </div>
        <div className="field"><label>Titel shift <span className="req">*</span></label>
          <input className="input" value={form.titel} onChange={e => setField('titel', e.target.value)} placeholder="Bijv. Begeleiding sportactiviteit" /></div>
        <div className="field"><label>Datum <span className="req">*</span></label>
          <input className="input" type="date" value={form.datum} onChange={e => setField('datum', e.target.value)} /></div>
        <div className="grid-2">
          <div className="field"><label>Tijd van</label>
            <input className="input" type="time" value={form.tijdVan} onChange={e => setField('tijdVan', e.target.value)} /></div>
          <div className="field"><label>Tijd tot</label>
            <input className="input" type="time" value={form.tijdTot} onChange={e => setField('tijdTot', e.target.value)} /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>Locatie</label>
            <select className="input" value={form.locatie} onChange={e => setField('locatie', e.target.value)}>
              <option value="">Kies locatie...</option>
              {['Edegem', 'Mortsel', 'Berchem'].map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="field"><label>Groep</label>
            <select className="input" value={form.groep} onChange={e => setField('groep', e.target.value)}>
              <option value="">Kies groep...</option>
              {['Groen', 'Geel', 'Rood', 'Blauw', 'Toekomstheld'].map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        </div>
        <div className="field"><label>Notitie</label>
          <textarea className="input" value={form.notitie} onChange={e => setField('notitie', e.target.value)} rows={2} /></div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Annuleren</button>
          <button className="btn btn-primary" onClick={() => {
            if (!form.userId || !form.titel || !form.datum) return alert('Vrijwilliger, titel en datum zijn verplicht');
            onSave(form);
          }}>Opslaan</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPlanning() {
  const [planning, setPlanning] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [filterUser, setFilterUser] = useState('');
  const [filterLocatie, setFilterLocatie] = useState('');

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    const [planSnap, userSnap] = await Promise.all([
      getDocs(collection(db, 'planning')),
      getDocs(collection(db, 'users')),
    ]);
    setPlanning(planSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.datum || '').localeCompare(b.datum || '')));
    setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  async function handleSave(form) {
    const data = { ...form, updatedAt: new Date().toISOString() };
    if (modal && modal.id) {
      await updateDoc(doc(db, 'planning', modal.id), data);
    } else {
      await addDoc(collection(db, 'planning'), { ...data, createdAt: new Date().toISOString() });
    }
    setModal(null);
    loadAll();
  }

  async function deletePlan(id) {
    if (!window.confirm('Verwijder deze shift?')) return;
    await deleteDoc(doc(db, 'planning', id));
    setPlanning(prev => prev.filter(p => p.id !== id));
  }

  const getUserName = (uid) => {
    const u = users.find(u => u.id === uid);
    if (!u) return uid;
    return u.voornaam ? `${u.voornaam} ${u.achternaam || ''}` : u.email;
  };

  const filtered = planning.filter(p => {
    if (filterUser && p.userId !== filterUser) return false;
    if (filterLocatie && p.locatie !== filterLocatie) return false;
    return true;
  });

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">📋 Planning beheer</h1>
          <p className="page-subtitle">Plan shiften in voor vrijwilligers</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}>+ Shift inplannen</button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <select className="input" style={{ maxWidth: 220 }} value={filterUser} onChange={e => setFilterUser(e.target.value)}>
          <option value="">Alle vrijwilligers</option>
          {users.filter(u => u.profileComplete).map(u => (
            <option key={u.id} value={u.id}>
              {u.voornaam ? `${u.voornaam} ${u.achternaam || ''}` : u.email}
            </option>
          ))}
        </select>
        <select className="input" style={{ maxWidth: 180 }} value={filterLocatie} onChange={e => setFilterLocatie(e.target.value)}>
          <option value="">Alle locaties</option>
          {['Edegem', 'Mortsel', 'Berchem'].map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      {loading ? <p style={{ color: 'var(--text-3)' }}>Laden...</p> : filtered.length === 0 ? (
        <div className="card card-padded" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Geen shiften gevonden</h3>
          <button className="btn btn-primary" onClick={() => setModal('new')}>+ Eerste shift inplannen</button>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Datum & tijd</th>
                <th>Vrijwilliger</th>
                <th>Shift</th>
                <th>Locatie</th>
                <th>Groep</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {item.datum ? format(new Date(item.datum), 'EEE d MMM yyyy', { locale: nl }) : '—'}
                    </div>
                    {item.tijdVan && (
                      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                        {item.tijdVan}{item.tijdTot ? ` – ${item.tijdTot}` : ''}
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{getUserName(item.userId)}</div>
                  </td>
                  <td>
                    <div>{item.titel}</div>
                    {item.notitie && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{item.notitie}</div>}
                  </td>
                  <td>{item.locatie ? <span className="badge badge-gray">{item.locatie}</span> : '—'}</td>
                  <td>
                    {item.groep
                      ? <span className="badge" style={{ background: (groepColors[item.groep] || '#888') + '20', color: groepColors[item.groep] || '#888' }}>{item.groep}</span>
                      : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setModal(item)}>✏</button>
                      <button className="btn btn-danger btn-sm" onClick={() => deletePlan(item.id)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <PlanModal
          item={modal === 'new' ? null : modal}
          users={users}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
